import React, { useState } from 'react';
import SearchIcon from './CustomIcons/SearchIcon';
import { CONTROL_TEXT_SIZE_CLASS } from './controlTextSize';
import { controlWidthStyle } from './controlWidth';

export type SearchSize = 'small' | 'medium' | 'large';
export type SearchVariant = 'default' | 'muted';

// Figma "Search Element - Standard": Small 28px, Medium 32px, Large 42px, with the radius
// tracking the tier (10/12/14) and the icon staying 15px throughout.
const SIZE_CLASSES: Record<SearchSize, string> = {
    small: 'h-[var(--malachite-control-height-small,28px)] gap-[6px] px-[10px] rounded-[var(--malachite-radius-10,10px)]',
    medium: 'h-[var(--malachite-control-height-medium,32px)] gap-[10px] px-[10px] rounded-xl',
    large: 'h-[var(--malachite-control-height-large,42px)] gap-[12px] px-[12px] rounded-[var(--malachite-radius-xxl,14px)]',
};

const ICON_SIZE = 15;

export interface SearchProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    /** 'large' (42px) for form inputs; 'medium' (32px) for toolbars; 'small' (28px) in a table. */
    size?: SearchSize;
    variant?: SearchVariant;
    disabled?: boolean;
    autoFocus?: boolean;
    className?: string;
    width?: string | number;
    style?: React.CSSProperties;
}

const Search: React.FC<SearchProps> = ({
    value,
    onChange,
    placeholder,
    size = 'large',
    variant = 'default',
    disabled = false,
    autoFocus = false,
    className = '',
    width,
    style,
}) => {
    const [focused, setFocused] = useState(false);
    const isMuted = variant === 'muted';

    // Muted variant: a borderless search that sits on a surface (no shadow). Default:
    // white field with border + subtle shadow. Both keep a visible focus border.
    // The fill is the Border colour by design; the dimmed grey vanishes on a sunken page.
    const bgClass = isMuted
        ? 'bg-[var(--malachite-card-border)]'
        : 'bg-[var(--malachite-card-background)]';
    const restingBorderClass = isMuted
        ? 'border-transparent'
        : 'border-[var(--malachite-card-border)]';
    // Hover darkens the border; focus (inline below) takes precedence.
    const hoverBorderClass = disabled ? '' : 'hover:border-[var(--malachite-text-placeholder)]';
    const iconColorClass = isMuted
        ? 'text-[var(--malachite-text-secondary)]'
        : 'text-[var(--malachite-text-placeholder)]';
    const placeholderColorClass = isMuted
        ? 'placeholder:text-[var(--malachite-text-secondary)]'
        : 'placeholder:text-[var(--malachite-text-placeholder)]';
    const boxShadow = isMuted
        ? 'none'
        : focused
          ? 'var(--malachite-shadow-accent, 0px 1px 0px 0px rgba(0, 175, 102, 0.15))'
          : '0px 1px 1px 0px rgba(0, 0, 0, 0.15)';

    return (
        <div
            className={`w-full ${bgClass} border border-solid ${restingBorderClass} ${hoverBorderClass} transition-colors flex items-center ${SIZE_CLASSES[size]} ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${className}`}
            style={{
                boxShadow,
                borderColor: focused
                    ? 'var(--malachite-control-border-hover, rgba(0, 175, 102, 0.4))'
                    : undefined,
                ...controlWidthStyle(width),
                ...style,
            }}
        >
            <SearchIcon size={ICON_SIZE} className={`${iconColorClass} shrink-0`} />
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder={placeholder}
                aria-label={placeholder ?? 'Search'}
                disabled={disabled}
                autoFocus={autoFocus}
                className={`flex-1 bg-transparent border-none outline-none ${CONTROL_TEXT_SIZE_CLASS[size]} font-medium text-[var(--malachite-text-primary)] ${placeholderColorClass} placeholder:font-medium disabled:cursor-not-allowed min-w-0`}
            />
        </div>
    );
};

export default Search;
